import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { quotesService, vehiclesService, customersService, financeService } from '../lib/services';
import { useToast } from '../lib/toast';
import { fmtMoneyCents } from './Quotes';

/* Line types + their default GST treatment (mirrors the backend). */
const LINE_TYPES = [
  { value: 'VEHICLE',     label: 'Vehicle',            taxable: true,  sign: 1 },
  { value: 'ACCESSORY',   label: 'Accessory / option', taxable: true,  sign: 1 },
  { value: 'DELIVERY',    label: 'Delivery',           taxable: true,  sign: 1 },
  { value: 'FNI_PRODUCT', label: 'F&I product',        taxable: true,  sign: 1 },
  { value: 'FEE',         label: 'Dealer fee',         taxable: true,  sign: 1 },
  { value: 'GOVT_CHARGE', label: 'Government charge',   taxable: false, sign: 1 },
  { value: 'DISCOUNT',    label: 'Discount',           taxable: true,  sign: -1 },
  { value: 'TRADE_IN',    label: 'Trade-in allowance', taxable: false, sign: -1 },
  { value: 'OTHER',       label: 'Other',              taxable: true,  sign: 1 },
];
const AU_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const LCT_THRESHOLD_CENTS = 6915200; // preview only; server is authoritative

const centsFromDollars = (s) => {
  if (s === '' || s == null) return 0;
  const n = Number(String(s).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};
const dollarsFromCents = (c) => (c == null ? '' : String(Math.round(c / 100)));
const typeMeta = (t) => LINE_TYPES.find((x) => x.value === t) || LINE_TYPES[LINE_TYPES.length - 1];

function unwrapList(resp) {
  return resp?.data?.content ?? resp?.content ?? (Array.isArray(resp?.data) ? resp.data : [])
      ?? (Array.isArray(resp) ? resp : []);
}

/** Client-side preview of the backend pricing formula (server recomputes on save). */
function computeTotals(lines, { lctApplies, tradeInPayoutCents, depositCents }) {
  let vehicleSubtotal = 0, onroad = 0, discount = 0, tradeAllowance = 0, gst = 0;
  for (const l of lines) {
    const qty = l.quantity > 0 ? l.quantity : 1;
    const amount = centsFromDollars(l.unitPriceDollars) * qty * typeMeta(l.lineType).sign;
    const lineGst = l.taxable ? Math.round(amount / 11) : 0;
    gst += lineGst;
    switch (l.lineType) {
      case 'VEHICLE': case 'ACCESSORY': case 'DELIVERY': case 'FNI_PRODUCT': case 'OTHER':
        vehicleSubtotal += amount; break;
      case 'FEE': case 'GOVT_CHARGE': onroad += amount; break;
      case 'DISCOUNT': discount += Math.abs(amount); break;
      case 'TRADE_IN': tradeAllowance += Math.abs(amount); break;
      default: break;
    }
  }
  const dutiable = Math.max(0, vehicleSubtotal - discount);
  const lct = lctApplies && dutiable > LCT_THRESHOLD_CENTS
    ? Math.round(0.33 * (dutiable - LCT_THRESHOLD_CENTS) / 1.1) : 0;
  const total = vehicleSubtotal + onroad - discount;
  const balance = total - tradeAllowance + (tradeInPayoutCents || 0) - (depositCents || 0);
  return { vehicleSubtotal, onroad, discount, tradeAllowance, gst, lct, total, balance };
}

let uidSeq = 1;
const newLine = (over = {}) => ({
  uid: uidSeq++, lineType: 'ACCESSORY', description: '', quantity: 1,
  unitPriceDollars: '', taxable: true, ...over,
});

export default function QuoteBuilder() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sp] = useSearchParams();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [quote, setQuote] = useState(null);         // server quote once saved
  const [vehicleList, setVehicleList] = useState([]);
  const [customerList, setCustomerList] = useState([]);

  // editable form state
  const [leadId, setLeadId] = useState(sp.get('leadId') || null);
  const [customerId, setCustomerId] = useState(sp.get('customerId') || '');
  const [vehicleId, setVehicleId] = useState(sp.get('vehicleId') || '');
  const [buyerState, setBuyerState] = useState('');
  const [lctApplies, setLctApplies] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [depositDollars, setDepositDollars] = useState('');
  const [tradePayoutDollars, setTradePayoutDollars] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([]);

  // finance
  const [finTerm, setFinTerm] = useState(5);
  const [finBalloon, setFinBalloon] = useState('');
  const [finance, setFinance] = useState(null);

  // sale completion
  const [saleOpen, setSaleOpen] = useState(false);
  const [saleDeposit, setSaleDeposit] = useState('');
  const [saleDelivery, setSaleDelivery] = useState('');
  const [saleDelivered, setSaleDelivered] = useState(false);

  const readOnly = quote && !['DRAFT', 'SENT'].includes(quote.status);

  // ---- load reference data + existing quote ----
  useEffect(() => {
    (async () => {
      try {
        const [veh, cust] = await Promise.all([
          vehiclesService.list({ page: 0, limit: 100 }).catch(() => null),
          customersService.list ? customersService.list({ page: 0, size: 100 }).catch(() => null) : null,
        ]);
        setVehicleList(unwrapList(veh));
        setCustomerList(unwrapList(cust));
      } catch { /* pickers optional */ }
    })();
  }, []);

  const hydrate = useCallback((q) => {
    setQuote(q);
    setLeadId(q.leadId || null);
    setCustomerId(q.customerId || '');
    setVehicleId(q.vehicleId || '');
    setBuyerState(q.buyerState || '');
    setLctApplies(!!q.lctApplies);
    setValidUntil(q.validUntil || '');
    setDepositDollars(dollarsFromCents(q.depositCents));
    setTradePayoutDollars(dollarsFromCents(q.tradeInPayoutCents));
    setNotes(q.notes || '');
    setLines((q.lines || []).map((l) => newLine({
      lineType: l.lineType,
      description: l.description,
      quantity: l.quantity,
      unitPriceDollars: dollarsFromCents(Math.abs(l.unitPriceCents)),
      taxable: l.taxable,
      refId: l.refId,
    })));
    if (q.financeMonthlyCents != null || q.financeWeeklyCents != null) {
      setFinance({
        weekly_aud: (q.financeWeeklyCents || 0) / 100,
        monthly_aud: (q.financeMonthlyCents || 0) / 100,
        term_years: q.financeTermYears,
        comparison_rate_pct: q.financeComparisonRatePct,
      });
      if (q.financeTermYears) setFinTerm(q.financeTermYears);
    }
  }, []);

  useEffect(() => {
    if (isNew) {
      // seed an empty vehicle line for a fresh quote
      setLines([newLine({ lineType: 'VEHICLE', description: '', quantity: 1 })]);
      return;
    }
    setLoading(true);
    quotesService.get(id)
      .then(hydrate)
      .catch((e) => toast(`Failed to load quote: ${e?.message || 'error'}`, { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [id, isNew, hydrate, toast]);

  // when a vehicle is picked on a fresh quote, prefill the VEHICLE line price
  const onPickVehicle = (vid) => {
    setVehicleId(vid);
    const v = vehicleList.find((x) => x.id === vid);
    if (v) {
      setLines((prev) => {
        const copy = [...prev];
        const idx = copy.findIndex((l) => l.lineType === 'VEHICLE');
        const line = newLine({
          lineType: 'VEHICLE',
          description: `${v.year || ''} ${v.make || ''} ${v.model || ''} ${v.trim || ''}`.trim(),
          quantity: 1,
          unitPriceDollars: dollarsFromCents(v.driveAwayPriceCents),
          refId: v.id,
        });
        if (idx >= 0) copy[idx] = line; else copy.unshift(line);
        return copy;
      });
    }
  };

  const totals = useMemo(() => computeTotals(lines, {
    lctApplies,
    tradeInPayoutCents: centsFromDollars(tradePayoutDollars),
    depositCents: centsFromDollars(depositDollars),
  }), [lines, lctApplies, tradePayoutDollars, depositDollars]);

  // ---- line editing ----
  const setLine = (uid, patch) => setLines((prev) => prev.map((l) => {
    if (l.uid !== uid) return l;
    const next = { ...l, ...patch };
    if (patch.lineType) next.taxable = typeMeta(patch.lineType).taxable;
    return next;
  }));
  const addLine = () => setLines((prev) => [...prev, newLine()]);
  const removeLine = (uid) => setLines((prev) => prev.filter((l) => l.uid !== uid));

  // ---- build request payload ----
  const buildPayload = () => ({
    leadId: leadId || null,
    customerId: customerId || null,
    vehicleId: vehicleId || null,
    buyerState: buyerState || null,
    lctApplies,
    validUntil: validUntil || null,
    depositCents: centsFromDollars(depositDollars),
    tradeInPayoutCents: centsFromDollars(tradePayoutDollars),
    notes: notes || null,
    financeType: finance ? 'CONSUMER_LOAN' : null,
    financeTermYears: finance ? finTerm : null,
    financeWeeklyCents: finance ? Math.round((finance.weekly_aud || 0) * 100) : null,
    financeMonthlyCents: finance ? Math.round((finance.monthly_aud || 0) * 100) : null,
    financeComparisonRatePct: finance ? finance.comparison_rate_pct : null,
    lines: lines.map((l, i) => ({
      lineType: l.lineType,
      description: l.description || typeMeta(l.lineType).label,
      quantity: l.quantity > 0 ? l.quantity : 1,
      unitPriceCents: centsFromDollars(l.unitPriceDollars) * typeMeta(l.lineType).sign,
      taxable: l.taxable,
      refId: l.refId || null,
      sortOrder: i,
    })),
  });

  const save = async () => {
    if (isNew && !customerId && !leadId) {
      toast('Pick a customer (or open the quote from a deal) before saving', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const saved = isNew
        ? await quotesService.create(payload)
        : await quotesService.update(quote.id, payload);
      hydrate(saved);
      toast(`Quote ${saved.quoteNumber} saved`, { variant: 'success' });
      if (isNew) navigate(`/quotes/${saved.id}`, { replace: true });
    } catch (e) {
      toast(`Save failed: ${e?.response?.data?.message || e?.message || 'error'}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const runFinance = async () => {
    const driveAway = totals.total / 100;
    if (!driveAway) { toast('Add a vehicle price first', { variant: 'error' }); return; }
    try {
      const data = await financeService.indicativeQuote({
        vehicleId: vehicleId || undefined,
        driveAwayPriceAud: driveAway,
        termYears: Number(finTerm) || 5,
        depositAud: centsFromDollars(depositDollars) / 100,
        // Net trade-in equity (allowance less any payout owing), so the amount
        // financed matches the balance payable on this quote.
        tradeInValueAud: (totals.tradeAllowance - centsFromDollars(tradePayoutDollars)) / 100,
        balloonPct: Number(finBalloon) || 0,
        financeType: 'CONSUMER_LOAN',
      });
      setFinance(data);
      toast('Finance recalculated', { variant: 'success' });
    } catch (e) {
      toast(`Finance failed: ${e?.message || 'error'}`, { variant: 'error' });
    }
  };

  const act = async (fn, okMsg) => {
    try {
      const saved = await fn();
      hydrate(saved);
      if (okMsg) toast(okMsg, { variant: 'success' });
    } catch (e) {
      toast(e?.response?.data?.message || e?.message || 'Action failed', { variant: 'error' });
    }
  };

  const downloadPdf = async () => {
    try {
      const blob = await quotesService.pdfBlob(quote.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast(`PDF failed: ${e?.message || 'error'}`, { variant: 'error' });
    }
  };

  const completeSale = async () => {
    try {
      const sale = await quotesService.completeSale({
        quoteId: quote.id,
        depositCents: saleDeposit ? centsFromDollars(saleDeposit) : undefined,
        deliveryDate: saleDelivery || undefined,
        markDelivered: saleDelivered,
      });
      setSaleOpen(false);
      toast(`Sale ${sale.saleNumber} created — vehicle marked sold`, { variant: 'success' });
      const refreshed = await quotesService.get(quote.id);
      hydrate(refreshed);
    } catch (e) {
      toast(e?.response?.data?.message || e?.message || 'Could not complete sale', { variant: 'error' });
    }
  };

  if (loading) {
    return (<><TopBar crumbs={['Deal Desk', 'Quote']} /><div className="app-content"><div className="empty-state" style={{ padding: 40 }}><div className="icon">⏳</div><div className="title">Loading…</div></div></div></>);
  }

  const status = quote?.status || 'DRAFT';

  return (
    <>
      <TopBar
        crumbs={['Deal Desk', quote ? quote.quoteNumber : 'New quote']}
        right={
          <>
            {quote && <button className="btn small" onClick={downloadPdf}>⭳ PDF</button>}
            {!readOnly && <button className="btn small primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
          </>
        }
      />
      <div className="app-content" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14, alignItems: 'start' }}>
        {/* LEFT — builder */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700 }}>Deal</div>
              {quote && <span className={`pill filled`}>{status}</span>}
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <Field label="Customer">
                <select value={customerId} disabled={readOnly || !!leadId} onChange={(e) => setCustomerId(e.target.value)}>
                  <option value="">{leadId ? '— from deal —' : 'Select customer'}</option>
                  {customerList.map((c) => {
                    // Identifiable label for the picker: name, else email/phone,
                    // else a short id — never a blank option (the old label
                    // produced " " for nameless customers, which read as empty).
                    const nm = [c.firstName || c.first_name, c.lastName || c.last_name]
                      .filter(Boolean).join(' ').trim();
                    const label = nm
                      || c.email || c.phoneE164 || c.phone
                      || `Customer ${String(c.id).slice(0, 8)}`;
                    return <option key={c.id} value={c.id}>{label}</option>;
                  })}
                </select>
              </Field>
              <Field label="Vehicle">
                <select value={vehicleId} disabled={readOnly} onChange={(e) => onPickVehicle(e.target.value)}>
                  <option value="">Vehicle TBC</option>
                  {vehicleList.map((v) => (
                    <option key={v.id} value={v.id}>
                      {`${v.year || ''} ${v.make || ''} ${v.model || ''} ${v.trim || ''}`.trim()} · {fmtMoneyCents(v.driveAwayPriceCents)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <Field label="Buyer state (for on-road charges)">
                <select value={buyerState} disabled={readOnly} onChange={(e) => setBuyerState(e.target.value)}>
                  <option value="">—</option>
                  {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Valid until">
                <input type="date" value={validUntil} disabled={readOnly} onChange={(e) => setValidUntil(e.target.value)} />
              </Field>
            </div>
          </div>

          {/* Lines */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px' }}>
              <div style={{ fontWeight: 700 }}>Line items</div>
              {!readOnly && <button className="btn small" onClick={addLine}>+ Add line</button>}
            </div>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Type</th>
                  <th>Description</th>
                  <th style={{ width: 60 }}>Qty</th>
                  <th style={{ width: 130 }}>Unit (AUD)</th>
                  <th style={{ width: 120 }}>Amount</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const amount = centsFromDollars(l.unitPriceDollars) * (l.quantity > 0 ? l.quantity : 1) * typeMeta(l.lineType).sign;
                  return (
                    <tr key={l.uid}>
                      <td>
                        <select className="input input-compact" value={l.lineType} disabled={readOnly} onChange={(e) => setLine(l.uid, { lineType: e.target.value })} style={{ width: '100%' }}>
                          {LINE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </td>
                      <td><input className="input input-compact" value={l.description} disabled={readOnly} placeholder={typeMeta(l.lineType).label} onChange={(e) => setLine(l.uid, { description: e.target.value })} style={{ width: '100%' }} /></td>
                      <td><input className="input input-compact" value={l.quantity} disabled={readOnly} inputMode="numeric" onChange={(e) => setLine(l.uid, { quantity: Number(e.target.value.replace(/[^\d]/g, '')) || 1 })} style={{ width: '100%' }} /></td>
                      <td><input className="input input-compact" value={l.unitPriceDollars} disabled={readOnly} inputMode="decimal" placeholder="0" onChange={(e) => setLine(l.uid, { unitPriceDollars: e.target.value.replace(/[^\d.]/g, '') })} style={{ width: '100%' }} /></td>
                      <td className="mono">{fmtMoneyCents(amount)}</td>
                      <td>{!readOnly && <button className="btn small" title="Remove" onClick={() => removeLine(l.uid)}>✕</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ padding: '10px 14px', display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--border, #eee)' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={lctApplies} disabled={readOnly} onChange={(e) => setLctApplies(e.target.checked)} />
                Luxury Car Tax applies
              </label>
              <Inline label="Trade-in payout owing">
                <input className="input input-compact" value={tradePayoutDollars} disabled={readOnly} inputMode="decimal" placeholder="0" onChange={(e) => setTradePayoutDollars(e.target.value.replace(/[^\d.]/g, ''))} style={{ width: 110 }} />
              </Inline>
              <Inline label="Deposit">
                <input className="input input-compact" value={depositDollars} disabled={readOnly} inputMode="decimal" placeholder="0" onChange={(e) => setDepositDollars(e.target.value.replace(/[^\d.]/g, ''))} style={{ width: 110 }} />
              </Inline>
            </div>
          </div>

          {/* Finance */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontWeight: 700 }}>Indicative finance</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Inline label="Term (yrs)"><input className="input input-compact" value={finTerm} inputMode="numeric" onChange={(e) => setFinTerm(Number(e.target.value.replace(/[^\d]/g, '')) || 1)} style={{ width: 70 }} /></Inline>
              <Inline label="Balloon %"><input className="input input-compact" value={finBalloon} inputMode="decimal" onChange={(e) => setFinBalloon(e.target.value.replace(/[^\d.]/g, ''))} style={{ width: 70 }} /></Inline>
              <button className="btn small btn-compact" onClick={runFinance}>Recalculate</button>
              {finance && (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  ~${finance.weekly_aud}/wk · ${finance.monthly_aud}/mo · comparison {finance.comparison_rate_pct}%
                </div>
              )}
            </div>
            <div className="text-xs text-muted" style={{ lineHeight: 1.5 }}>
              Indicative only — not an offer or approval of credit. Repayments are an estimate using an
              indicative rate and standard fees; actual rates, fees and repayments depend on the lender and
              your credit assessment, and are subject to lender approval. NCCP disclosures are printed on the PDF.
            </div>
          </div>

          <div className="card">
            <Field label="Notes (internal / printed on quote)">
              <textarea value={notes} disabled={readOnly} rows={3} onChange={(e) => setNotes(e.target.value)} style={{ width: '100%' }} />
            </Field>
          </div>
        </div>

        {/* RIGHT — totals + actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 12 }}>
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Summary</div>
            <Row k="Vehicle & options" v={fmtMoneyCents(totals.vehicleSubtotal)} />
            {totals.discount > 0 && <Row k="Less discount" v={'-' + fmtMoneyCents(totals.discount)} />}
            {totals.onroad !== 0 && <Row k="On-road & govt" v={fmtMoneyCents(totals.onroad)} />}
            <Row k="Drive-away price" v={fmtMoneyCents(totals.total)} strong />
            <Row k="of which GST" v={fmtMoneyCents(totals.gst)} muted />
            {lctApplies && totals.lct > 0 && <Row k="of which LCT" v={fmtMoneyCents(totals.lct)} muted />}
            {totals.tradeAllowance > 0 && <Row k="Less trade-in" v={'-' + fmtMoneyCents(totals.tradeAllowance)} />}
            {centsFromDollars(tradePayoutDollars) > 0 && <Row k="Plus payout owing" v={fmtMoneyCents(centsFromDollars(tradePayoutDollars))} />}
            {centsFromDollars(depositDollars) > 0 && <Row k="Less deposit" v={'-' + fmtMoneyCents(centsFromDollars(depositDollars))} />}
            <Row k="Balance payable" v={fmtMoneyCents(totals.balance)} strong />

            {(() => {
              const sv = vehicleList.find((v) => v.id === vehicleId);
              const cost = sv?.costPriceCents;
              if (!cost) return null;
              const net = totals.vehicleSubtotal - totals.discount;   // sellable revenue ex on-road
              const gross = net - cost;
              const pct = net > 0 ? Math.round((gross / net) * 100) : 0;
              const col = gross < 0 ? 'var(--danger)' : gross === 0 ? 'var(--text-secondary)' : 'var(--success)';
              return (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border-color, #e5e7eb)' }}>
                  <Row k="Vehicle cost" v={fmtMoneyCents(cost)} muted />
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>Est. gross margin</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: col }}>{fmtMoneyCents(gross)} · {pct}%</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {quote && (
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontWeight: 700 }}>Actions</div>
              {status === 'DRAFT' && <button className="btn small primary" onClick={() => act(() => quotesService.send(quote.id), 'Quote sent — vehicle reserved')}>Send to customer</button>}
              {(status === 'SENT' || status === 'DRAFT') && <button className="btn small" onClick={() => act(() => quotesService.accept(quote.id), 'Marked accepted')}>Mark accepted</button>}
              {['DRAFT', 'SENT', 'ACCEPTED'].includes(status) && <button className="btn small primary" onClick={() => { setSaleDeposit(dollarsFromCents(quote.depositCents)); setSaleOpen(true); }}>Complete sale…</button>}
              {(status === 'SENT') && <button className="btn small" onClick={() => act(() => quotesService.decline(quote.id, 'Declined by customer'), 'Marked declined')}>Mark declined</button>}
              {['DRAFT', 'SENT'].includes(status) && <button className="btn small" onClick={() => act(() => quotesService.cancel(quote.id), 'Quote cancelled')}>Cancel quote</button>}
              <button className="btn small" onClick={downloadPdf}>Download PDF</button>
            </div>
          )}
        </div>
      </div>

      {saleOpen && (
        <div className="modal-backdrop" onClick={() => setSaleOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(440px, 92vw)' }}>
            <div className="modal-header"><div style={{ fontWeight: 700 }}>Complete sale</div><button className="modal-close" onClick={() => setSaleOpen(false)}>✕</button></div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field label="Deposit taken (AUD)"><input value={saleDeposit} inputMode="decimal" onChange={(e) => setSaleDeposit(e.target.value.replace(/[^\d.]/g, ''))} /></Field>
              <Field label="Delivery date"><input type="date" value={saleDelivery} onChange={(e) => setSaleDelivery(e.target.value)} /></Field>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={saleDelivered} onChange={(e) => setSaleDelivered(e.target.checked)} />
                Mark delivered now (advance deal to Delivered)
              </label>
              <div className="text-xs text-muted">Creates a sale, marks the vehicle SOLD and advances the deal stage.</div>
            </div>
            <div className="modal-footer">
              <button className="btn small" onClick={() => setSaleOpen(false)}>Cancel</button>
              <button className="btn small primary" onClick={completeSale}>Complete sale</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="form-field" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</label>
      {children}
    </div>
  );
}
function Inline({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{label}</span>
      {children}
    </div>
  );
}
function Row({ k, v, strong, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: strong ? '6px 0' : '1px 0', borderTop: strong ? '1px solid var(--border, #e5e7eb)' : 'none' }}>
      <span style={{ fontSize: strong ? 14 : 13, fontWeight: strong ? 700 : 400, color: muted ? 'var(--text-secondary)' : 'var(--text-primary)' }}>{k}</span>
      <span style={{ fontSize: strong ? 14 : 13, fontWeight: strong ? 700 : 500, color: muted ? 'var(--text-secondary)' : 'var(--text-primary)' }}>{v}</span>
    </div>
  );
}
